import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Grid,
  Typography,
} from "@mui/material";
import { useToast } from '../../context/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CharityCard from './CharityCard';
import CreateCharityModal from './CreateCharityModal';
import { getCharities } from '../../api/charityApi';
import { useLocalText } from '../common/useLocalText';
import StatusTabs from '../approval/StatusTabs';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../../utils/versionedContent';

const CharityCardList = ({ charities, setCharities, onFetch, overrideSpaceId }) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/charities', 'charity-edit');
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabValue, setTabValue] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const searchPlaceholder = useLocalText("Charity Name");
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const fetchCharities = async () => {
    try {
      const res = onFetch ? await onFetch() : await getCharities();
      setCharities(res.data || res);
    } catch (err) {
      console.error("Failed to fetch charities:", err);
    }
  };

  useEffect(() => {
    if (onFetch) fetchCharities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const versioned = useMemo(() => buildVersionedList(charities), [charities]);
  const counts    = useMemo(() => countVersionedTabs(charities),  [charities]);

  const filteredCharities = useMemo(() => {
    let result = filterVersionedList(versioned, tabValue);
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return result;
    return result.filter((c) =>
      c.localizedName?.values?.some((v) => v.value?.toLowerCase().includes(q)) ||
      c.websiteUrl?.toLowerCase().includes(q)
    );
  }, [versioned, debouncedSearch, tabValue]);

  return (
    <>
      <StatusTabs items={charities} counts={counts} value={tabValue} onChange={setTabValue} highlightTab={highlight.tab} highlightToken={highlight.token} />

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ maxWidth: 320, flexGrow: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            inputRef={inputRef}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#85a5c6" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                bgcolor: "white",
                "& fieldset": { borderColor: "#dde6f0" },
                "&:hover fieldset": { borderColor: "#5383b3" },
                "&.Mui-focused fieldset": { borderColor: "#0a326d" },
              },
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={2}>
        {filteredCharities.map((c) => (
          <Grid key={c._viewType === 'snapshot' ? `snap-${c._liveId}` : c.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CharityCard
              onSuccess={(targetTab) => { fetchCharities(); if (targetTab) flashTab(targetTab); }}
              {...c}
              overrideSpaceId={overrideSpaceId}
            />
          </Grid>
        ))}

        {/* Add charity slot tile — editors only */}
        {canEdit && (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <Box
            onClick={() => setOpen(true)}
            sx={{
              width: "100%",
              minHeight: 360,
              border: "2px dashed",
              borderColor: "#c8d8ea",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              cursor: "pointer",
              bgcolor: "white",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#EAF0F6",
                borderColor: "#5383b3",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                bgcolor: "#EAF0F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddIcon sx={{ color: "#0a326d", fontSize: 30 }} />
            </Box>
            <Typography variant="body2" sx={{ color: "#5383b3", fontWeight: 600 }}>
              Add Charity
            </Typography>
          </Box>
        </Grid>
        )}
      </Grid>

      {debouncedSearch && filteredCharities.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6, color: "#85a5c6", fontWeight: 500 }}>
          No charities match "{inputRef.current?.value ?? debouncedSearch}"
        </Box>
      )}

      <CreateCharityModal
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={() => {
          fetchCharities();
          toast.success("Charity created successfully");
          setOpen(false);
          flashTab('draft');
        }}
      />

    </>
  );
};

export default CharityCardList;
